import { Button, Card, Image, message, Modal, Spin, Upload } from "antd";
import React, { useState } from "react";
import useAxios from "../../../hooks/useAxios";
import {
  CheckCircleFilled,
  FileTextOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import CropImage from "../components/CropImage";
import StatusIndicator, {
  VerifiedIndicator,
} from "./components/StatusIndicator";
import StatusMessage from "./components/StatusMessage";
import { capitalizeSentence } from "../utils/commonFunctions";

const MB = 3;

const Photo = ({
  application,
  loadApplication,
  url,
  uploadedFile,
  aiSettings,
}) => {
  const ID = application?._id;
  const [load, setLoad] = useState(false);
  const api = useAxios();
  const [file, setFile] = useState(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [modal, setModal] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(16 / 9);
  const [crop, setCrop] = useState();
  const [imgSrc, setImgSrc] = useState("");
  const [error, setError] = useState("");

  const toggle = () => setModal(!modal);
  const previewToggle = () => setPreviewModal(!previewModal);

  const resetCrop = () => {
    setImgSrc("");
    setCrop(undefined);
    setAspectRatio(16 / 9);
    setFile(null);
  };

  function onSelectFile(fileData) {
    const { file, fileList } = fileData;
    if (file && fileList.length > 0) {
      if (file.size > MB * 1024 * 1024) {
        message.warning(`Image size must be less than ${MB}MB`, 2);
        return;
      }
      setCrop(undefined);
      const aspect = 130 / 140;
      setAspectRatio(aspect);
      setFile(file);
      const reader = new FileReader();
      reader.addEventListener(
        "load",
        () => (setImgSrc(reader.result?.toString() || ""), toggle())
      );
      reader.readAsDataURL(file);
    }
  }

  const onClickImgHandler = async (file) => {
    setError("");
    const form = new FormData();
    form.append("candidatePhoto", file);
    setLoad(true);
    try {
      const result = await api.put(`/candidate/candidatePhoto/${ID}`, form, {
        tokenType: "candidate",
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      const { response } = result?.data?.OCRDetails;

      const promise = [loadApplication(false, false, "photo")];
      await Promise.all(promise);
      if (aiSettings?.isActive) {
        if ("error" in response) {
          setError(response?.error);
        } else {
          message.success(`Photo uploaded successfully`, 2);
        }
      } else {
        if ("error" in response) {
          setError(response?.error);
        }
        message.success(`Photo uploaded successfully`, 2);
      }
      resetCrop();
    } catch (err) {
      console.log(err?.response?.data?.error?.OCRDetails?.response?.error);
      setError(err?.response?.data?.error?.OCRDetails?.response?.error);
      setLoad(false);
    } finally {
      setLoad(false);
    }
  };

  const uploaded = uploadedFile && uploadedFile?.length !== 0;

  return (
    <Spin
      style={{ backgroundColor: "rgba(0, 0, 0, 0.47)" }}
      spinning={load}
      tip={
        <div>
          <p className="text-white font-weight-normal">
            Uploading another file won't affect this one.
          </p>
        </div>
      }
      size="large"
    >
      <Card
        className={`mb-4 bg-white`}
        style={{ border: "1px solid grey" }}
        title={null}
      >
        <div className="d-flex flex-column px-4" style={{ gap: 10 }}>
          <div className="d-flex justify-content-center">
            <StatusIndicator
              filledStatus={application?.photoFilledStatus}
              status={application?.candidatePhotoStatus}
            />
          </div>
          <div>
            <h5 className="text-dark text-center font-weight-bold mb-1">
              Photo
            </h5>
            <StatusMessage
              filledStatus={application?.photoFilledStatus}
              status={application?.candidatePhotoStatus}
              reason={application?.candidatePhotoRejection}
              type={"Photo"}
            />
          </div>
          <div>
            {uploaded && (
              <hr
                style={{ borderTop: "1px solid #D5D5D5" }}
                className="mb-3"
              ></hr>
            )}
            <div className="d-flex align-items-center flex-column gap-1">
              {uploaded && (
                <Image
                  style={{ objectFit: "contain" }}
                  src={url}
                  width={200}
                  height={100}
                />
              )}
              {!application?.candidatePhotoStatus ? (
                <Upload
                  listType="picture"
                  maxCount={1}
                  fileList={null}
                  onChange={(value) => onSelectFile(value)}
                  onPreview={(file) => {
                    previewToggle();
                  }}
                  showUploadList={{
                    showRemoveIcon: false,
                  }}
                  //   disabled={}
                  beforeUpload={(file) => {
                    const isValid =
                      file.type === "image/png" ||
                      file.type === "image/jpeg" ||
                      file.type === "image/jpg";

                    if (!isValid) {
                      message.error(
                        "Only PNG, JPG, JPEG and JFIF files are allowed."
                      );
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  accept="image/png, image/jpg, image/jpeg"
                >
                  <div className="d-flex align-items-center justify-content-between w-100">
                    <Button
                      data-testid = "upload-photo"
                      style={{ border: "1px dashed #6c757d" }}
                      icon={<UploadOutlined />}
                    >
                      {uploaded ? "Re Upload Photo" : "Upload Document"}
                    </Button>
                  </div>
                </Upload>
              ) : (
                <VerifiedIndicator />
              )}
              {error && <p className="text-danger">{capitalizeSentence(error)}</p>}
            </div>
          </div>
        </div>
      </Card>
      <Modal
        title={`Crop Aadhar Card`}
        centered
        open={modal}
        zIndex={9}
        onOk={() => toggle()}
        closable={false}
        width={700}
        footer={null}
        className="crop-ant-modal-body"
      >
        <CropImage
          key={file}
          setCrop={setCrop}
          crop={crop}
          imgSrc={imgSrc}
          file={file}
          toggle={toggle}
          aspectRatio={aspectRatio}
          setAspectRatio={setAspectRatio}
          onClickImgHandler={onClickImgHandler}
        />
      </Modal>
    </Spin>
  );
};

export default Photo;
